/**
 * The editor globe (Alan 2026-10-01): one calm, constant turn; every editor
 * arrives at once as a wave on their country, then whoever doesn't fit the brief
 * is cut away and the top matches pulse. Canvas for the land dots, DOM pins so a
 * portrait stays crisp. One rAF loop, no React re-render per frame: pins move
 * through refs.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { landDots, project } from "./globe.ts";
import worldTexture from "../../assets/demo/world-2_5d.webp";
import pointingHand from "../../assets/demo/hand-2_5d.webp";

export interface GlobeEditor {
  id: string;
  initial: string;
  photoUrl?: string | null;
  firstName?: string | null;
  lat: number | null;
  lng: number | null;
}

function hash(id: string): number {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h;
}
const DOTS = landDots();

export function EditorPortrait({ editor }: { editor: Pick<GlobeEditor, "initial" | "photoUrl"> }) {
  const [failed, setFailed] = useState(false);
  return editor.photoUrl && !failed
    ? <img src={editor.photoUrl} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    : <b>{editor.initial}</b>;
}
/** Where the view is centred mid-stage: Germany, tilted so Central Europe faces us. */
export const LOCK = { lng: 10, tilt: 42 };
/**
 * One calm, constant turn the whole stage long (Alan 2026-10-01: no sudden whirl
 * elsewhere). ~25° over the match stage, centred on LOCK, so Europe never leaves.
 */
const DRIFT_DEG_PER_MS = 0.004;
const DRIFT_START = LOCK.lng + 13;

/**
 * The 2.5D world (Codex Round 2 R3, equirectangular 2:1) wrapped onto the sphere.
 * Tilt never changes, so each screen pixel's latitude row, longitude offset and
 * shading are solved once; a frame is then one add + one lookup per pixel.
 * Until (or unless) the texture loads, the land-dot map draws instead.
 */
const TEX_W = 1024, TEX_H = 512;
function loadTexture(): Promise<Uint8ClampedArray | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = TEX_W; c.height = TEX_H;
      const g = c.getContext("2d");
      if (!g) return resolve(null);
      g.drawImage(img, 0, 0, TEX_W, TEX_H);
      resolve(g.getImageData(0, 0, TEX_W, TEX_H).data);
    };
    img.onerror = () => resolve(null);
    img.src = worldTexture;
  });
}
interface SphereMap { px: number; idx: Int32Array; row: Int32Array; lng: Float32Array; shade: Float32Array; alpha: Uint8Array }
/** Inverse of `project`: for every pixel on the disc, which latitude row and longitude offset it shows. */
function sphereMap(px: number, radiusPx: number, tilt: number): SphereMap {
  const t = tilt * (Math.PI / 180), idx: number[] = [], row: number[] = [], lng: number[] = [], shade: number[] = [], alpha: number[] = [];
  for (let j = 0; j < px; j++) for (let i = 0; i < px; i++) {
    const x = (i + 0.5 - px / 2) / radiusPx, y = -(j + 0.5 - px / 2) / radiusPx, d2 = x * x + y * y;
    if (d2 > 1) continue;
    const z = Math.sqrt(1 - d2);
    const y0 = y * Math.cos(t) + z * Math.sin(t), z0 = -y * Math.sin(t) + z * Math.cos(t);
    const lat = Math.asin(Math.max(-1, Math.min(1, y0))) * (180 / Math.PI);
    idx.push(j * px + i);
    row.push(Math.min(TEX_H - 1, Math.floor(((90 - lat) / 180) * TEX_H)));
    lng.push(Math.atan2(x, z0) * (180 / Math.PI));
    // Matte 2.5D light: soft key from the top left, gentle falloff to the rim.
    const key = Math.max(0, -0.45 * x + 0.5 * y + 0.74 * z);
    shade.push(0.42 + 0.5 * z + 0.28 * key);
    alpha.push(Math.round(Math.min(1, (1 - Math.sqrt(d2)) * radiusPx * 1.5) * 255));
  }
  return { px, idx: Int32Array.from(idx), row: Int32Array.from(row), lng: Float32Array.from(lng), shade: Float32Array.from(shade), alpha: Uint8Array.from(alpha) };
}

export interface PinSpot { x: number; y: number; dx: number; dy: number; scale: number }

/** Where an editor's own spot is for a given view: their country, or the rim in its direction when it faces away. */
function anchorOf(e: GlobeEditor, centerLng: number, tilt: number, r: number, rim: number, unknownAt: number, unknownOf: number): { x: number; y: number; z: number } {
  if (e.lat === null || e.lng === null) {
    const a = (-172 + (164 * (unknownAt + 0.5)) / Math.max(1, unknownOf)) * (Math.PI / 180);
    return { x: Math.cos(a) * rim, y: Math.sin(a) * rim, z: 0.4 };
  }
  const p = project(e.lng, e.lat, centerLng, tilt);
  const len = Math.hypot(p.x, p.y) || 1, edge = rim * 0.96;
  if (p.z < 0.15 || len * r > edge) return { x: (p.x / len) * edge, y: (p.y / len) * edge, z: Math.max(0.15, Math.min(p.z, 0.4)) };
  return { x: p.x * r, y: p.y * r, z: p.z };
}

/**
 * Where each editor stands (Alan 2026-10-01): on their real country, nudged apart
 * until no two circles overlap — accuracy gives way to legibility. Solved once for
 * the mid-stage view; `dx/dy` is each pin's nudge, carried along while the globe
 * turns. Countries on the far side sit on the rim in their direction. Pixels from
 * the globe centre; `r` = projection radius, `rim` = the visible sphere's edge.
 * ponytail: O(n²) relaxation, fine for ~100 pins; a grid would be needed past ~500.
 */
export function layoutPins(editors: GlobeEditor[], r: number, pin: number, rim = r): Map<string, PinSpot> {
  const unknown = editors.filter((e) => e.lat === null || e.lng === null);
  const pts = editors.map((e) => {
    const a = anchorOf(e, LOCK.lng, LOCK.tilt, r, rim, unknown.indexOf(e), unknown.length);
    return { id: e.id, x: a.x, y: a.y, ax: a.x, ay: a.y, scale: 0.72 + 0.28 * a.z };
  });
  for (let it = 0; it < 360; it++) {
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const a = pts[i]!, b = pts[j]!;
        const min = ((a.scale + b.scale) / 2) * pin + 2;
        let dx = b.x - a.x, dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= min) continue;
        if (d < 0.01) { dx = Math.cos(i + j); dy = Math.sin(i + j); d = 1; } // same spot: split deterministically
        const push = (min - d) / 2;
        a.x -= (dx / d) * push; a.y -= (dy / d) * push;
        b.x += (dx / d) * push; b.y += (dy / d) * push;
      }
    }
    // A soft pull home keeps clusters on their country; the last 60 rounds only separate.
    const pull = it < 300 ? 0.03 : 0;
    for (const p of pts) {
      p.x += (p.ax - p.x) * pull; p.y += (p.ay - p.y) * pull;
      const d = Math.hypot(p.x, p.y), max = rim * 1.04;
      if (d > max) { p.x *= max / d; p.y *= max / d; }
    }
  }
  return new Map(pts.map((p) => [p.id, { x: p.x, y: p.y, dx: p.x - p.ax, dy: p.y - p.ay, scale: p.scale }]));
}

/**
 * The finale (Alan 2026-10-01): the hand taps the top three open on the globe, one
 * after another. Per pin: glide there, press, the pin opens, let go.
 */
const TAP_MS = 480;
/** How far apart two opened pins sit, centre to centre — room for both name labels. */
const OPEN_GAP = 74;
/** The R5 artwork's fingertip, as a fraction of its 56 px box (Codex). */
const TIP = { x: 0.3 * 56, y: 0.08 * 56 };

export function Globe({ editors, activeIds, openIds, paused, reduced, size = 340 }: {
  editors: GlobeEditor[];
  /** Pins that qualify; the rest are cut away (staggered). */
  activeIds: ReadonlySet<string>;
  /** The top matches, in order; once non-empty the hand taps them open. */
  openIds: readonly string[];
  paused: boolean;
  reduced: boolean;
  size?: number;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pinRefs = useRef(new Map<string, HTMLDivElement>());
  const pinPx = size < 300 ? 18 : 24;
  const r = (size / 2) * 0.92, rim = size * 0.46;
  const spots = useMemo(() => layoutPins(editors, r, pinPx, rim), [editors, r, pinPx, rim]);
  const unknown = useMemo(() => editors.filter((e) => e.lat === null || e.lng === null), [editors]);
  // All of them arrive at once, as one wave from the centre (Germany) outwards.
  const ripple = useMemo(() => new Map([...spots].sort((a, b) => Math.hypot(a[1].x, a[1].y) - Math.hypot(b[1].x, b[1].y)).map(([id], i) => [id, i])), [spots]);
  const state = useRef({ lng: reduced ? LOCK.lng : DRIFT_START, paused, reduced, openIds, tapMs: 0, off: {} as Record<string, { x: number; y: number }> });
  state.current.paused = paused;
  state.current.reduced = reduced;
  state.current.openIds = openIds;
  const handRef = useRef<HTMLImageElement>(null);

  const [tex, setTex] = useState<Uint8ClampedArray | null>(null);
  useEffect(() => { let live = true; void loadTexture().then((t) => { if (live) setTex(t); }); return () => { live = false; }; }, []);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    // Textured: 1.5× is plenty for a soft relief map and keeps a frame cheap on phones.
    const dpr = Math.min(tex ? 1.5 : 2, window.devicePixelRatio || 1);
    el.width = Math.round(size * dpr);
    el.height = Math.round(size * dpr);
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const map = tex ? sphereMap(el.width, r * dpr, LOCK.tilt) : null;
    const frame = map ? ctx.createImageData(el.width, el.height) : null;
    let raf = 0;
    let last = performance.now();
    const half = size / 2;
    const draw = (now: number) => {
      const dt = Math.min(100, now - last);
      last = now;
      const s = state.current;
      if (s.reduced) s.lng = LOCK.lng;
      else if (!s.paused) s.lng -= dt * DRIFT_DEG_PER_MS;
      if (map && frame && tex) {
        const out = frame.data;
        for (let k = 0; k < map.idx.length; k++) {
          let u = ((map.lng[k]! + s.lng + 180) / 360) % 1;
          if (u < 0) u += 1;
          const src = (map.row[k]! * TEX_W + Math.floor(u * TEX_W)) * 4, dst = map.idx[k]! * 4, sh = map.shade[k]!;
          out[dst] = tex[src]! * sh; out[dst + 1] = tex[src + 1]! * sh; out[dst + 2] = tex[src + 2]! * sh; out[dst + 3] = map.alpha[k]!;
        }
        ctx.putImageData(frame, 0, 0);
      } else {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, size, size);
        ctx.save();
        ctx.beginPath(); ctx.arc(half, half, half * 0.99, 0, Math.PI * 2); ctx.clip();
        for (const [lng, lat] of DOTS) {
          const p = project(lng, lat, s.lng, LOCK.tilt);
          if (p.z <= 0) continue;
          ctx.globalAlpha = 0.18 + 0.62 * p.z;
          ctx.fillStyle = "#f3e6d2";
          ctx.beginPath();
          ctx.arc(half + p.x * r, half + p.y * r, 1.05 + 0.5 * p.z, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      // Pins ride their country; each keeps the nudge that stops it overlapping.
      const at = new Map<string, { x: number; y: number }>();
      for (const e of editors) {
        const spot = spots.get(e.id);
        if (!spot) continue;
        const a = anchorOf(e, s.lng, LOCK.tilt, r, rim, unknown.indexOf(e), unknown.length);
        at.set(e.id, { x: half + a.x + spot.dx, y: half + a.y + spot.dy });
      }
      // The finale: glide → press → open, pin by pin. Its own clock, so it is smooth at 60 fps and pauses.
      const hand = handRef.current, targets = s.openIds;
      if (!targets.length) s.tapMs = 0;
      else if (!s.paused) s.tapMs += dt;
      const steps = s.reduced ? targets.length : Math.min(targets.length, Math.floor((s.tapMs + TAP_MS * 0.3) / TAP_MS));
      // Opened pins grow and carry a name: neighbours (Saskia/Tobias) glide apart until the labels fit.
      const opened = targets.slice(0, steps).filter((id) => at.has(id));
      const goal = new Map(opened.map((id) => [id, { x: 0, y: 0 }]));
      for (let it = 0; it < 4; it++) for (let i = 0; i < opened.length; i++) for (let j = i + 1; j < opened.length; j++) {
        const A = at.get(opened[i]!)!, B = at.get(opened[j]!)!, ga = goal.get(opened[i]!)!, gb = goal.get(opened[j]!)!;
        const dx = B.x + gb.x - (A.x + ga.x), dy = B.y + gb.y - (A.y + ga.y), d = Math.hypot(dx, dy) || 1;
        if (d >= OPEN_GAP) continue;
        const push = (OPEN_GAP - d) / 2;
        ga.x -= (dx / d) * push; ga.y -= (dy / d) * push; gb.x += (dx / d) * push; gb.y += (dy / d) * push;
      }
      for (const [id, p] of at) {
        const o = (s.off[id] ??= { x: 0, y: 0 }), g = goal.get(id) ?? { x: 0, y: 0 };
        o.x += (g.x - o.x) * 0.18; o.y += (g.y - o.y) * 0.18;
        p.x += o.x; p.y += o.y;
        const node = pinRefs.current.get(id);
        if (node) node.style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;
      }
      targets.forEach((id, j) => pinRefs.current.get(id)?.classList.toggle("is-open", j < steps));
      for (const e of editors) if (!targets.includes(e.id)) pinRefs.current.get(e.id)?.classList.remove("is-open");
      if (hand) {
        if (!targets.length || s.reduced) hand.style.opacity = "0";
        else {
          const k = Math.min(targets.length - 1, Math.floor(s.tapMs / TAP_MS)), f = Math.min(1, s.tapMs / TAP_MS - k);
          const to = at.get(targets[k]!) ?? { x: half, y: size };
          const from = k === 0 ? { x: half + r * 0.35, y: size + 10 } : at.get(targets[k - 1]!) ?? to;
          const g = f < 0.55 ? f / 0.55 : 1, e2 = g < 0.5 ? 2 * g * g : 1 - Math.pow(-2 * g + 2, 2) / 2;
          const press = f >= 0.55 && f < 0.8 ? 0.9 : 1;
          // After the last tap the hand lets go and drifts off, so the third name is readable.
          const gone = s.tapMs > targets.length * TAP_MS;
          const lift = gone ? Math.min(1, (s.tapMs - targets.length * TAP_MS) / 300) : 0;
          hand.style.opacity = gone ? "0" : "1";
          hand.style.transform = `translate3d(${from.x + (to.x - from.x) * e2 - TIP.x + lift * 18}px, ${from.y + (to.y - from.y) * e2 - TIP.y + lift * 26}px, 0) scale(${press})`;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [size, editors, spots, unknown, r, rim, tex]);
  const ids = openIds.join(",");
  useEffect(() => { state.current.tapMs = 0; }, [ids]);

  return (
    <div className="dm-globe" style={{ width: size, height: size }} aria-hidden="true">
      <div className="dm-globe-shell" />
      <canvas ref={canvas} style={{ width: size, height: size }} />
      {editors.map((e) => {
        const p = spots.get(e.id)!;
        return (
          <div
            key={e.id}
            className={`dm-pin${activeIds.has(e.id) ? "" : " is-out"}`}
            style={{ transform: `translate3d(${size / 2 + p.x}px, ${size / 2 + p.y}px, 0)`, "--s": p.scale.toFixed(2), "--pin": `${pinPx}px`, "--d": `${120 + (ripple.get(e.id) ?? 0) * 12}ms`, "--o": `${(hash(e.id) % 9) * 60}ms` } as CSSProperties}
            ref={(n) => { if (n) pinRefs.current.set(e.id, n); else pinRefs.current.delete(e.id); }}
          >
            <span><EditorPortrait editor={e} /></span>
            {openIds.includes(e.id) && <em className="dm-pin-name">{e.firstName ?? "Editor"}</em>}
          </div>
        );
      })}
      <img ref={handRef} className="dm-globe-hand" src={pointingHand} width={56} height={56} alt="" aria-hidden="true" draggable={false} />
    </div>
  );
}
